import { useState, useRef, useEffect } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useColorScheme } from '../hooks/useColorScheme';
import { SCHEMES } from '../utils/colors';
import Logo from './Logo';

export default function Header({ dashboards, currentDashboard, onSwitchDashboard, lastUpdated }) {
  const { user, logout } = useAuth();
  const { schemeId, setSchemeId } = useColorScheme();
  const [showSwitcher, setShowSwitcher] = useState(false);
  const [showSchemes, setShowSchemes] = useState(false);
  const switcherRef = useRef(null);
  const schemeRef = useRef(null);

  useEffect(() => {
    const handleClick = (e) => {
      if (switcherRef.current && !switcherRef.current.contains(e.target)) {
        setShowSwitcher(false);
      }
      if (schemeRef.current && !schemeRef.current.contains(e.target)) {
        setShowSchemes(false);
      }
    };
    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, []);

  const meta = currentDashboard;

  return (
    <div style={headerStyle}>
      <div style={leftStyle}>
        <div style={logoGroupStyle}>
          <Logo size={36} />
          <div>
            <div style={logoMainStyle}>trendency</div>
            <div style={logoSubStyle}>research</div>
          </div>
        </div>
        <div style={dividerStyle}></div>
        <div style={clientStyle}>{meta?.client || ''}</div>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
        {/* Dashboard Switcher */}
        {dashboards.length > 1 && (
          <div ref={switcherRef} style={{ position: 'relative' }}>
            <button
              onClick={(e) => { e.stopPropagation(); setShowSwitcher(!showSwitcher); }}
              style={switcherBtnStyle}
            >
              <span style={stateBadgeStyle}>{meta?.stateCode}</span>
              {meta?.label}
              <span style={{ fontSize: '10px', color: 'rgba(255,255,255,0.5)', marginLeft: '4px' }}>&#9662;</span>
            </button>

            {showSwitcher && (
              <div style={dropdownStyle}>
                <div style={dropdownLabelStyle}>Switch Dashboard</div>
                {dashboards.map(d => (
                  <div
                    key={d.id}
                    style={{
                      ...optionStyle,
                      ...(d.id === meta?.id ? selectedOptionStyle : {})
                    }}
                    onClick={() => { onSwitchDashboard(d); setShowSwitcher(false); }}
                    onMouseEnter={(e) => { if (d.id !== meta?.id) e.target.style.background = '#f7f8fa'; }}
                    onMouseLeave={(e) => { if (d.id !== meta?.id) e.target.style.background = 'transparent'; }}
                  >
                    <span>{d.label}</span>
                    <span style={{
                      fontSize: '11px', fontWeight: 700, letterSpacing: '0.5px',
                      color: d.id === meta?.id ? '#2ec4b6' : '#8a94a3'
                    }}>{d.stateCode}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {dashboards.length === 1 && (
          <span style={{ color: 'rgba(255,255,255,0.9)', fontSize: '13px', fontWeight: 500 }}>
            {meta?.label}
          </span>
        )}

        {/* Status */}
        <div style={statusStyle}>
          <div style={statusDotStyle}></div>
          <span style={statusTextStyle}>
            {meta?.label} &middot; Last updated {lastUpdated || 'today'}
          </span>
        </div>

        {/* Chart color scheme (color-vision accessibility) */}
        <div ref={schemeRef} style={{ position: 'relative' }}>
          <button
            onClick={(e) => { e.stopPropagation(); setShowSchemes(!showSchemes); }}
            style={logoutBtnStyle}
            title="Chart color scheme (color-vision accessibility)"
            aria-label="Chart color scheme"
          >
            <span aria-hidden="true" style={{ marginRight: '6px' }}>&#9681;</span>
            Colors
          </button>

          {showSchemes && (
            <div style={dropdownStyle}>
              <div style={dropdownLabelStyle}>Chart Colors</div>
              {Object.values(SCHEMES).map(s => (
                <div
                  key={s.id}
                  style={{
                    ...optionStyle,
                    ...(s.id === schemeId ? selectedOptionStyle : {})
                  }}
                  onClick={() => { setSchemeId(s.id); setShowSchemes(false); }}
                  onMouseEnter={(e) => { if (s.id !== schemeId) e.currentTarget.style.background = '#f7f8fa'; }}
                  onMouseLeave={(e) => { if (s.id !== schemeId) e.currentTarget.style.background = s.id === schemeId ? 'rgba(46,196,182,0.08)' : 'transparent'; }}
                >
                  <span>{s.label}</span>
                  <span style={{ display: 'flex', gap: '3px', marginLeft: '12px' }}>
                    {s.palette.slice(0, 4).map(c => (
                      <span key={c} style={{ width: '10px', height: '10px', borderRadius: '3px', background: c, display: 'inline-block' }} />
                    ))}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* User menu */}
        {user && (
          <button
            onClick={logout}
            style={logoutBtnStyle}
            onMouseEnter={(e) => e.target.style.background = 'rgba(255,255,255,0.18)'}
            onMouseLeave={(e) => e.target.style.background = 'rgba(255,255,255,0.1)'}
          >
            Sign Out
          </button>
        )}
      </div>
    </div>
  );
}

// Styles
const headerStyle = {
  background: 'linear-gradient(135deg, #0f4c5c 0%, #1a6b7a 100%)',
  padding: '14px 28px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  boxShadow: '0 2px 12px rgba(0,0,0,0.15)'
};

const leftStyle = { display: 'flex', alignItems: 'center', gap: '16px' };
const logoGroupStyle = { display: 'flex', alignItems: 'center', gap: '10px' };
const logoMainStyle = { fontSize: '22px', fontWeight: 700, color: '#fff', letterSpacing: '-0.03em', lineHeight: 1 };
const logoSubStyle = { fontSize: '9px', color: 'rgba(255,255,255,0.55)', textTransform: 'uppercase', letterSpacing: '3.5px', marginTop: '1px' };
const dividerStyle = { width: '1px', height: '32px', background: 'rgba(255,255,255,0.2)', margin: '0 8px' };
const clientStyle = { color: 'rgba(255,255,255,0.9)', fontSize: '14px', fontWeight: 500 };

const switcherBtnStyle = {
  display: 'flex', alignItems: 'center', gap: '8px',
  background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)',
  borderRadius: '8px', padding: '6px 14px', cursor: 'pointer',
  color: 'rgba(255,255,255,0.9)', fontSize: '13px', fontWeight: 500, fontFamily: 'inherit'
};

const stateBadgeStyle = {
  background: 'rgba(46,196,182,0.25)', color: '#2ec4b6',
  fontSize: '11px', fontWeight: 700, padding: '2px 7px',
  borderRadius: '4px', letterSpacing: '0.5px'
};

const dropdownStyle = {
  position: 'absolute', top: 'calc(100% + 6px)', right: 0, zIndex: 60,
  background: '#fff', borderRadius: '10px', padding: '4px', minWidth: '240px',
  maxHeight: '400px', overflowY: 'auto',
  boxShadow: '0 8px 30px rgba(0,0,0,0.2)', border: '1px solid #e2e6ea'
};

const dropdownLabelStyle = {
  padding: '8px 12px 4px', fontSize: '10px', textTransform: 'uppercase',
  letterSpacing: '0.5px', color: '#8a94a3', fontWeight: 600
};

const optionStyle = {
  padding: '10px 12px', borderRadius: '6px', cursor: 'pointer',
  fontSize: '13px', color: '#1a2332', display: 'flex',
  alignItems: 'center', justifyContent: 'space-between'
};

const selectedOptionStyle = {
  fontWeight: 600, background: 'rgba(46,196,182,0.08)', color: '#0f4c5c'
};

const statusStyle = {
  display: 'flex', alignItems: 'center', gap: '12px',
  background: 'rgba(255,255,255,0.1)', borderRadius: '8px', padding: '6px 14px'
};
const statusDotStyle = { width: '8px', height: '8px', borderRadius: '50%', background: '#2ec4b6' };
const statusTextStyle = { color: 'rgba(255,255,255,0.8)', fontSize: '12px' };

const logoutBtnStyle = {
  background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)',
  borderRadius: '8px', padding: '6px 14px', color: 'rgba(255,255,255,0.8)',
  fontSize: '12px', fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit'
};
